import { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { squarify } from '@/utils/squarify';
import { CORE, SCALES } from '@/utils/dls-tokens';
import type { PortfolioMetrics } from '@/types/metrics';

interface PortfolioTreemapProps {
  portfolios: PortfolioMetrics[];
  selectedPortfolio: string | null;
  onSelect: (portfolio: string | null) => void;
}

function getSeverityColor(p: PortfolioMetrics): string {
  if (p.unreachableNodes === 0) return CORE.neeveGreen;
  const offlineRatio = p.unreachableNodes / p.totalNodes;
  const offlineDaysCapped = Math.min(p.longestOfflineDays / 7, 1);
  const severity = offlineRatio * 0.6 + offlineDaysCapped * 0.4;

  if (severity < 0.33) return SCALES.red[300];
  if (severity < 0.66) return SCALES.red[500];
  return SCALES.red[900];
}

function getTextColor(bgColor: string): string {
  // Light red backgrounds need dark text, dark backgrounds need light text
  if (bgColor === SCALES.red[300]) return '#1f2937';
  return '#ffffff';
}

function formatDays(days: number): string {
  if (days >= 1) return `${Math.round(days * 10) / 10}d`;
  const hours = days * 24;
  if (hours >= 1) return `${Math.round(hours)}h`;
  return `${Math.round(hours * 60)}m`;
}

export function PortfolioTreemap({
  portfolios,
  selectedPortfolio,
  onSelect,
}: PortfolioTreemapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState({ w: 0, h: 0 });
  const [tooltip, setTooltip] = useState<{
    portfolio: PortfolioMetrics;
    x: number;
    y: number;
  } | null>(null);

  const updateBounds = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setBounds({ w: rect.width, h: rect.height });
    }
  }, []);

  useEffect(() => {
    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [updateBounds]);

  const items = portfolios
    .filter((r) => r.totalNodes > 0)
    .map((r) => ({ label: r.portfolio || 'Unassigned', value: r.totalNodes }));

  const rects = squarify(items, { x: 0, y: 0, w: bounds.w, h: bounds.h });

  const portfolioMap = new Map(portfolios.map((r) => [r.portfolio || 'Unassigned', r]));

  return (
    <Card>
      <CardHeader className="dls-flex dls-flex-row dls-items-center dls-justify-between">
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          Portfolio Overview
          <InfoTooltip text="Treemap sized by Node count. Green = all online. Red intensity = severity (offline ratio + duration). Click to filter dashboard by portfolio." />
        </CardTitle>
        <span className="dls-text-sm dls-font-normal dls-text-muted-foreground">
          {portfolios.length} Portfolios
        </span>
      </CardHeader>
      <CardContent>
        <div
          ref={containerRef}
          className="dls-relative"
          style={{ height: '320px' }}
          onMouseLeave={() => setTooltip(null)}
        >
          {rects.map((rect) => {
            const p = portfolioMap.get(rect.label);
            if (!p) return null;

            const bgColor = getSeverityColor(p);
            const textColor = getTextColor(bgColor);
            const isSelected = selectedPortfolio === p.portfolio;
            const minDimension = Math.min(rect.w, rect.h);
            const showOffline = minDimension > 50 && p.unreachableNodes > 0;
            const showCount = minDimension > 35;
            const labelSize =
              minDimension > 80 ? 'dls-text-sm' : minDimension > 50 ? 'dls-text-xs' : 'dls-text-[10px]';

            return (
              <div
                key={rect.label}
                className={`dls-absolute dls-cursor-pointer dls-transition-all dls-duration-150 dls-overflow-hidden dls-flex dls-flex-col dls-items-center dls-justify-center ${
                  isSelected ? 'dls-ring-2 dls-ring-white dls-z-10' : 'hover:brightness-110'
                }`}
                style={{
                  left: `${rect.x}px`,
                  top: `${rect.y}px`,
                  width: `${rect.w - 2}px`,
                  height: `${rect.h - 2}px`,
                  backgroundColor: bgColor,
                  color: textColor,
                  margin: '1px',
                  borderRadius: '4px',
                }}
                onClick={() => onSelect(isSelected ? null : p.portfolio)}
                onMouseEnter={(e) => {
                  const containerRect = containerRef.current?.getBoundingClientRect();
                  if (containerRect) {
                    setTooltip({
                      portfolio: p,
                      x: e.clientX - containerRect.left,
                      y: e.clientY - containerRect.top,
                    });
                  }
                }}
                onMouseMove={(e) => {
                  const containerRect = containerRef.current?.getBoundingClientRect();
                  if (containerRect) {
                    setTooltip((prev) =>
                      prev
                        ? {
                            ...prev,
                            x: e.clientX - containerRect.left,
                            y: e.clientY - containerRect.top,
                          }
                        : null
                    );
                  }
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                <span
                  className={`dls-font-semibold ${labelSize} dls-leading-tight dls-text-center dls-px-1 dls-max-w-full dls-break-words dls-line-clamp-2`}
                >
                  {rect.label === 'Unknown' ? 'Unassigned' : rect.label}
                </span>
                {showCount && <span className="dls-text-[10px] dls-opacity-80">{p.totalNodes} Nodes</span>}
                {showOffline && (
                  <span className="dls-text-[10px] dls-opacity-90 dls-font-medium">
                    {p.unreachableNodes} offline
                  </span>
                )}
              </div>
            );
          })}

          {/* Tooltip */}
          {tooltip && (
            <div
              className="dls-absolute dls-z-20 dls-pointer-events-none dls-bg-popover dls-text-popover-foreground dls-border dls-rounded-lg dls-shadow-40 dls-p-3 dls-text-sm"
              style={{
                left: `${Math.min(tooltip.x + 12, bounds.w - 220)}px`,
                top: `${Math.min(tooltip.y + 12, bounds.h - 120)}px`,
                minWidth: '200px',
              }}
            >
              <p className="dls-font-semibold dls-mb-1">{tooltip.portfolio.portfolio || 'Unassigned'}</p>
              <div className="dls-space-y-0.5 dls-text-xs">
                <p>
                  <span className="dls-text-muted-foreground">Total:</span>{' '}
                  {tooltip.portfolio.totalNodes} nodes
                </p>
                <p>
                  <span className="dls-text-muted-foreground">Online:</span>{' '}
                  <span className="dls-text-status-online">{tooltip.portfolio.aliveNodes}</span>
                </p>
                {tooltip.portfolio.unreachableNodes > 0 && (
                  <>
                    <p>
                      <span className="dls-text-muted-foreground">Offline:</span>{' '}
                      <span className="dls-text-status-offline">{tooltip.portfolio.unreachableNodes}</span>
                    </p>
                    <p>
                      <span className="dls-text-muted-foreground">Uptime:</span>{' '}
                      {tooltip.portfolio.uptimePercent.toFixed(1)}%
                    </p>
                  </>
                )}
                {tooltip.portfolio.longestOfflineDays > 0 &&
                  tooltip.portfolio.longestOfflineNodeName && (
                    <p className="dls-pt-1 dls-border-t dls-mt-1">
                      <span className="dls-text-muted-foreground">Longest offline:</span>{' '}
                      {tooltip.portfolio.longestOfflineNodeName} (
                      {formatDays(tooltip.portfolio.longestOfflineDays)})
                    </p>
                  )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
